import Link from "./router-link";
import { ArrowUpRight } from "lucide-react";
import { CTA, PageHero, SectionHeading } from "./site-shell";
import type { LandingPageData } from "../lib/landing-pages";
import { serializeJsonLd, siteUrl } from "../lib/seo";

export function LandingPage({
  page,
  relatedPages,
}: {
  page: LandingPageData;
  relatedPages: LandingPageData[];
}) {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${siteUrl}${page.path}#service`,
    name: page.pageTitle,
    description: page.description,
    url: `${siteUrl}${page.path}`,
    image: `${siteUrl}${page.image}`,
    serviceType: page.pageTitle,
    provider: { "@id": `${siteUrl}/#organization` },
    areaServed: ["中国", "全球"],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(serviceJsonLd) }}
      />
      <PageHero
        compact
        eyebrow={page.eyebrow}
        title={page.pageTitle}
        description={page.description}
        image={page.image}
      />

      <section className="section-shell section-block-sm">
        <SectionHeading
          eyebrow="执行范围"
          title={page.sectionTitle}
          description={page.summary}
        />
        <div className="seo-detail-grid">
          {page.items.map((item, index) => (
            <article className="seo-detail-item" key={item.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-muted section-block-sm">
        <div className="section-shell seo-audience-layout">
          <SectionHeading
            eyebrow="适用判断"
            title={page.audienceTitle}
            description="根据企业目标、现有基础与资源条件确定实施范围，优先解决当前最影响增长的关键问题。"
          />
          <ol className="seo-audience-list">
            {page.audience.map((item, index) => (
              <li key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {page.acceptance && page.acceptanceTitle && (
        <section className="section-shell section-block-sm">
          <SectionHeading
            eyebrow="验收与边界"
            title={page.acceptanceTitle}
            description="项目开始前确认范围、负责人、交付物和验收标准；以正式报价单与双方合同为准。"
          />
          <div className="seo-acceptance-list">
            {page.acceptance.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="section-shell section-block-sm">
        <SectionHeading eyebrow="相关方案" title="继续查看相关增长路径" />
        <nav className="seo-related-links" aria-label="相关增长路径">
          {relatedPages.map((relatedPage) => (
            <Link href={relatedPage.path} key={relatedPage.path}>
              <span>{relatedPage.pageTitle}</span>
              <ArrowUpRight size={18} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      </section>

      <CTA
        title="从当前业务问题出发，确定最值得优先推进的增长动作。"
        description="预约初步增长诊断，沟通产品、市场、网站与获客基础，明确适合当前阶段的实施范围。"
      />
    </>
  );
}
